import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Container,
  useTheme,
  Alert,
} from '@mui/material';
import {
  Email,
  LinkedIn,
  GitHub,
  Send,
  LocationOn,
  Palette,
} from '@mui/icons-material';

const Contact = () => {
  const theme = useTheme();
  const formStartTime = useRef(Date.now());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    // Honeypot field - hidden from humans but bots will fill it
    website: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mathChallenge, setMathChallenge] = useState({ num1: 0, num2: 0, answer: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Generate random math challenge
  useEffect(() => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setMathChallenge({ num1, num2, answer: '' });
  }, []);

  // Phone number validation regex - accepts various formats
  const phoneRegex = /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;

  const validatePhone = (phone) => {
    if (!phone) return ''; // Phone is optional
    if (!phoneRegex.test(phone)) {
      return 'Please enter a valid phone number (e.g., 123-456-7890, (123) 456-7890, or +1-123-456-7890)';
    }
    return '';
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    // Phone validation
    const phoneError = validatePhone(formData.phone);
    if (phoneError) {
      newErrors.phone = phoneError;
    }

    // Math challenge validation
    const expectedAnswer = mathChallenge.num1 + mathChallenge.num2;
    if (!mathChallenge.answer || parseInt(mathChallenge.answer) !== expectedAnswer) {
      newErrors.mathChallenge = `Please solve: ${mathChallenge.num1} + ${mathChallenge.num2} = ?`;
    }

    // Honeypot validation - if this field is filled, it's likely a bot
    if (formData.website.trim()) {
      newErrors.honeypot = 'Bot detected';
    }

    // Time-based validation - form should take at least 3 seconds to fill
    const timeSpent = (Date.now() - formStartTime.current) / 1000;
    if (timeSpent < 3) {
      newErrors.timing = 'Please take your time filling out the form';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const handleMathChange = (e) => {
    setMathChallenge({
      ...mathChallenge,
      answer: e.target.value,
    });
    
    if (errors.mathChallenge) {
      setErrors({
        ...errors,
        mathChallenge: '',
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prevent multi-clicking
    if (isSubmitting) {
      return;
    }
    
    if (!validateForm()) {
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
      return;
    }
    
    setIsSubmitting(true);
    setShowError(false);
    
    console.log('Sending form data:', formData);
    
    // Use relative URL for production, or localhost for development
    const apiUrl = process.env.NODE_ENV === 'production' 
      ? '/api/contact' 
      : 'http://localhost:3001/api/contact';
    
    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        // Send additional anti-spam data
        honeypot: formData.website,
        timeSpent: (Date.now() - formStartTime.current) / 1000,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      })
    })
    .then(res => res.json())
    .then(data => {
      console.log(data);
      setIsSubmitting(false);
      
      if (data.success !== false) {
        // Clear form on successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          website: '',
        });
        setErrors({});
        setFormSubmitted(true);
        
        // Generate new math challenge
        const num1 = Math.floor(Math.random() * 10) + 1;
        const num2 = Math.floor(Math.random() * 10) + 1;
        setMathChallenge({ num1, num2, answer: '' });
        formStartTime.current = Date.now();
      } else {
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      }
    })
    .catch(err => {
      console.error(err);
      setIsSubmitting(false);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    });
  };

  const contactInfo = [
    {
      icon: <LinkedIn />,
      title: 'LinkedIn',
      value: 'linkedin.com/in/dhineline',
      link: 'https://www.linkedin.com/in/dhineline/',
    },
    {
      icon: <GitHub />,
      title: 'GitHub',
      value: 'github.com/dhineline',
      link: 'https://github.com/dhineline',
    },
    {
      icon: <Palette />,
      title: 'DeviantArt',
      value: 'dhineline.deviantart.com',
      link: 'https://dhineline.deviantart.com',
    },
    {
      icon: <LocationOn />,
      title: 'Location',
      value: 'Central Indiana',
      link: 'https://maps.google.com/?q=Indianapolis,IN',
    },
  ];

  return (
    <Box
      id="contact"
      sx={{
        py: 8,
        background: `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Let's Connect
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            Ready to collaborate on your next project? I'd love to hear from you!
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {!formSubmitted ? (
                  <>
                    <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
                      Get In Touch
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                      Whether you have a project in mind, want to discuss potential opportunities, 
                      or just want to say hello, I'm always open to new connections and collaborations.
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit} sx={{ space: 3 }}>
                      <TextField
                        fullWidth
                        label="Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        error={!!errors.name}
                        helperText={errors.name}
                        sx={{ mb: 3 }}
                      />
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        error={!!errors.email}
                        helperText={errors.email}
                        sx={{ mb: 3 }}
                      />
                      <TextField
                        fullWidth
                        label="Phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        error={!!errors.phone}
                        helperText={errors.phone || "Optional - Format: 123-456-7890"}
                        placeholder="123-456-7890"
                        sx={{ mb: 3 }}
                      />
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        error={!!errors.message}
                        helperText={errors.message}
                        sx={{ mb: 3 }}
                      />
                      <TextField
                        fullWidth
                        label={`Math Challenge: ${mathChallenge.num1} + ${mathChallenge.num2} = ?`}
                        name="mathChallenge"
                        type="number"
                        value={mathChallenge.answer}
                        onChange={handleMathChange}
                        required
                        error={!!errors.mathChallenge}
                        helperText={errors.mathChallenge || "Please solve this simple math problem to prove you're human"}
                        sx={{ mb: 3 }}
                      />
                      {/* Honeypot field - hidden from humans but bots will fill it */}
                      <TextField
                        fullWidth
                        label="Website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        sx={{ 
                          display: 'none',
                          position: 'absolute',
                          left: '-9999px',
                          opacity: 0,
                          pointerEvents: 'none'
                        }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        startIcon={<Send />}
                        fullWidth
                        disabled={isSubmitting}
                        sx={{ py: 1.5 }}
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography 
                      variant="h3" 
                      fontWeight={700} 
                      sx={{ 
                        mb: 3,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Thanks for reaching out.
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color="text.secondary" 
                      sx={{ 
                        lineHeight: 1.6,
                        maxWidth: 400,
                        mx: 'auto'
                      }}
                    >
                      I'm probably knee deep in a web project or feeding my awnry children but I'll follow up very soon!
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
                Contact Information
              </Typography>
              
              <Grid container spacing={3}>
                {contactInfo.map((info, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      sx={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            color: 'white',
                            display: 'inline-flex',
                            mb: 2,
                          }}
                        >
                          {info.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                          {info.title}
                        </Typography>
                        {info.link ? (
                          <Button
                            href={info.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="text"
                            sx={{ color: 'text.secondary', textTransform: 'none' }}
                          >
                            {info.value}
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {info.value}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  Available for:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                  {['Full-time Opportunities', 'Contract Work', 'Consulting', 'Open Source Collaboration'].map((item, index) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        background: `linear-gradient(45deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`,
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            I typically respond within 24 hours. Looking forward to connecting with you!
          </Typography>
        </Box>

        {showError && (
          <Alert severity="error" sx={{ mt: 2, mx: 'auto', maxWidth: 600 }}>
            There was an error sending the message. Please try again later.
          </Alert>
        )}
      </Container>
    </Box>
  );
};

export default Contact; 